import { useState } from 'react';
import { Logo } from './Logo';
import { Mail, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ContactModal } from './ContactModal';

// X (Twitter) icon component
const XIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export function Footer() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const links = {
    product: [
      { label: 'Features', href: '#features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Enterprise', href: '/enterprise' },
      { label: 'For Sales', href: '#sales' },
      { label: 'For Recruiters', href: '#recruiters' },
      { label: 'For Job Seekers', href: '#jobseekers' }
    ],
    company: [
      { label: 'About', href: '#about' }
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Cookie Policy', href: '/cookies' }
    ]
  };

  const socialLinks = [
    { icon: XIcon, href: 'https://x.com/xrepl_ai', label: 'X (Twitter)' },
    { icon: Linkedin, href: 'https://www.linkedin.com/company/111069329', label: 'LinkedIn' },
    { icon: Mail, href: 'mailto:saxa@xrepl.ai', label: 'Email' }
  ];

  return (
    <>
      <footer className="bg-white/80 dark:bg-[#0A1628]/80 border-t border-gray-200 dark:border-[#1E3A8A]/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <Logo size="md" />
                <div>
                  <span className="font-bold text-xl text-[#1E3A8A] dark:text-white block">
                    xRepl.ai
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Smart Email Intelligence
                  </span>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-sm">
                AI-powered email replies that sound human, build rapport, and convert — right in Gmail & LinkedIn.
              </p>
              <div className="flex gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target={social.href.startsWith('mailto:') ? undefined : '_blank'}
                    rel={social.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                    className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-[#1E3A8A]/20 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-[#60A5FA] hover:text-white transition-all"
                    aria-label={social.label}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Product</h4>
              <ul className="space-y-3">
                {links.product.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-gray-600 dark:text-gray-400 hover:text-[#60A5FA] transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Company</h4>
              <ul className="space-y-3">
                {links.company.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-gray-600 dark:text-gray-400 hover:text-[#60A5FA] transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => setIsContactOpen(true)}
                    className="text-gray-600 dark:text-gray-400 hover:text-[#60A5FA] transition-colors"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Legal</h4>
              <ul className="space-y-3">
                {links.legal.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-gray-600 dark:text-gray-400 hover:text-[#60A5FA] transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-gray-200 dark:border-[#1E3A8A]/30 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              © 2026 xRepl.ai. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-gray-600 dark:text-gray-400 hover:text-[#60A5FA] transition-colors text-sm"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="text-gray-600 dark:text-gray-400 hover:text-[#60A5FA] transition-colors text-sm"
              >
                Terms
              </Link>
              <button
                onClick={() => setIsContactOpen(true)}
                className="text-gray-600 dark:text-gray-400 hover:text-[#60A5FA] transition-colors text-sm"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>

      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
}
